<script setup>
  import { computed } from 'vue'
  import { useStormStore } from "@/stores/StormStore";
  import { useUserStore } from "@/stores/UserStore";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import MapStormSelectionButtonStormCenter from "@/components/StormSelectionButtonStormCenter.vue";
  import MapAdSlot from "@/components/MapAdSlot.vue";
  import BannerAd from "@/components/BannerAd.vue";
  import FooterBannerAd from "@/components/FooterBannerAd.vue";
  import WebViewVersionNumber from '@/components/WebViewVersionNumber.vue';
  import backgroundImage from '../../pages/storm-center-webview/assets/images/hurricane-1.webp';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import useAdConfigurationGlobals from '@/composables/useAdConfigurationGlobals';
  import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
  import ModalBrandPromo from '@/components/ModalBrandPromo.vue';

  const { $WEB_VIEW, $PLATFORM, $PROMO_MODAL_ALLOWED } = useSettingsGlobals();
  const { $ADS_ALLOWED } = useAdConfigurationGlobals();

  setPageDatasets();  

  const stormStore = useStormStore();
  const userStore = useUserStore();
  const isDesktop = useMediaQuery('(min-width: 840px)');
  const isXLDesktop = useMediaQuery('(min-width: 1280px)');

  const adType = computed(() => isXLDesktop.value ? 'med-rect' : 'header');
  const showPromoModal = computed(() => userStore.messages && userStore.messages.length > 0 && parseInt(userStore.nonTrialMemberLevel) < 49 && $PROMO_MODAL_ALLOWED);

  await stormStore.getStormsData();

  function setPageDatasets() {
    const body = document.documentElement;
  
    if ($WEB_VIEW) {
      body.dataset.webView = true;
      body.dataset.platform = $PLATFORM.toLowerCase();
    }
  }
</script>

<template>
  <div class="jwx-page jwx-page--cover-image jwx-page--cover-shade jwx-page--center jwx-overflow-auto" :style="`background-image: url(${backgroundImage})`">
    <div class="jwx-storm-center__container jwx-page__container">
      <h2 class="jwx-page__title jwx-page__title--light jwx-page__title--jumbo jwx-text-center jwx-text-uppercase jwx-text-italic">StormTrack</h2>
      <div class="jwx-storm-center__content" :data-ads-allowed="$ADS_ALLOWED">
        <div class="jwx-storm-center__atlantic jwx-page-block-container">
          <h3 class="jwx-page-block-title jwx-page-block-title--light">Atlantic Storms</h3>
          <div class="jwx-page-block jwx-page-block--translucent">
            <ul
              v-if="stormStore.activeAtlanticStorms.length > 0"
              class="jwx-source-card-list block-card-list"
            >
              <li
                v-for="storm in stormStore.activeAtlanticStorms" 
                :key="storm.id"
                class="jwx-source-card"
              >
                <MapStormSelectionButtonStormCenter
                  :storm="storm"
                  :showActive="false"
                />
              </li>
            </ul>
            <p
              v-else
              class="jwx-storm-center__notice-text"
            >No active storms at this time.</p>            
          </div>
        </div>
        <div class="jwx-storm-center__eastern-pacific jwx-page-block-container">
          <h3 class="jwx-page-block-title jwx-page-block-title--light">Eastern Pacific Storms</h3>
          <div class="jwx-page-block jwx-page-block--translucent">
            <ul
              v-if="stormStore.activeEasternPacificStorms.length > 0"
              class="jwx-source-card-list block-card-list"
            >
              <li
                v-for="storm in stormStore.activeEasternPacificStorms" 
                :key="storm.id"
                class="jwx-source-card"
              >
                <MapStormSelectionButtonStormCenter
                  :storm="storm"
                  :showActive="false"
                />
              </li>
            </ul>
            <p
              v-else
              class="jwx-storm-center__notice-text"
            >No active storms at this time.</p>  
          </div>
        </div>
        <div v-if="$ADS_ALLOWED" class="jwx-storm-center__banner-ad">
          <MapAdSlot v-if="isXLDesktop" adSlot="map_med_rect" />
          <BannerAd v-if="isDesktop && !isXLDesktop" :type="adType" />
        </div>
      </div>
      <WebViewVersionNumber styleClasses="jwx-storm-center__version" />
    </div>
  </div>
  <FooterBannerAd />
  <Suspense>
    <ModalSiteSettings />
  </Suspense>  
  <ModalBrandPromo v-if="showPromoModal" />
</template>

<style>
  body {
    padding: 0;
    margin: 0;
  }
</style>